"use client"

import { useState } from "react"
import api from "../api/api"
import { useNavigate, useLocation } from "react-router-dom"
import SearchTable from "./SearchTable"
import LoadingSpinner from "../utils/LoadingSpinner"
import { Search, ChevronRight, ArrowRight } from "lucide-react"

const RelatedWordSearch = ({
  onDashboardClick,
  selectedCompanies,
  onCompanyClick,
  addAllCompanies,
  registeredCompanies,
  setCompanyDescription,
  handleOpenAnalysis,
  handleDescriptionClick,
  isMobile,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchName, setSearchName] = useState("")
  const [companies, setCompanies] = useState(null)
  const [showSpinner, setShowSpinner] = useState(false)
  const [resultSearchName, setResultSearchName] = useState("")

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      if (showSpinner) return

      searchCompanies()
    }
  }

  const searchCompanies = async () => {
    if (!searchName.trim()) return
    setShowSpinner(true)
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/chat/api/search-companies-by-related-word/${searchName}/`,
        {},
      )
      console.log(response.data)
      setCompanies(response.data)
      setResultSearchName(searchName)
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // 未認証の場合、ログイン画面にリダイレクト
        navigate("/login", { state: { from: location.pathname } })
      } else {
        console.error("Error fetching company info:", error)
      }
    }
    setShowSpinner(false)
  }

  // 統合された検索フォーム
  const renderSearchForm = () => (
    <div className={isMobile ? "p-3" : ""} id={!isMobile ? "search-company-form" : ""}>
      <div className={`relative flex items-center ${!isMobile ? "search-company-input" : ""}`}>
        <div className={`flex-grow relative ${!isMobile ? "textarea-icon-container" : ""}`}>
          <input
            type="text"
            placeholder="例) 生成AI 半導体 ..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={handleKeyDown}
            className={
              isMobile
                ? "w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent"
                : "search-area w-full pl-10 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
            }
            disabled={showSpinner}
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        </div>
        <button
          className={`ml-2 ${isMobile ? "p-2 rounded-full" : "search-company-button flex justify-center items-center"} ${
            showSpinner ? "bg-gray-300 text-gray-500" : "bg-gray-800 text-white hover:bg-gray-700 transition-colors"
          }`}
          type="button"
          onClick={searchCompanies}
          disabled={showSpinner}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  )

  // モバイル用の検索結果ヘッダー
  const renderMobileResultHeader = () => (
    <div className="px-4 py-2 flex justify-between items-center">
      <div className="text-gray-700 font-medium">「{resultSearchName}」に関連する企業</div>
      {selectedCompanies && selectedCompanies.length > 0 && (
        <button
          onClick={handleOpenAnalysis}
          className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          分析を表示
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )

  // デスクトップ用の検索結果ヘッダー（既存のスタイルを維持）
  const renderDesktopResultHeader = () => (
    <div className="ml-4 flex flex-wrap gap-4">
      <div className="flex items-center">「{resultSearchName}」に関連する企業 (関連度の高い順)</div>
      {selectedCompanies && selectedCompanies.length > 0 && (
        <div
          className="show-analysis-sm p-1 bg-green-500 w-[120px] flex align-center items-center text-white rounded-2xl cursor-pointer"
          onClick={handleOpenAnalysis}
        >
          分析画面を開く
        </div>
      )}
    </div>
  )

  return (
    <div className={isMobile ? "" : "tabcontent-search"}>
      <div className={isMobile ? "" : "analysis-container"}>
        <div className={isMobile ? "" : "search-container main-contents-container"}>
          {/* 統合された検索フォーム */}
          {renderSearchForm()}
        </div>

        {/* ローディングスピナー */}
        {showSpinner ? (
          <div className={isMobile ? "flex justify-center py-8" : "modal-background"}>
            <LoadingSpinner />
          </div>
        ) : (
          companies && (
            <>
              {/* 検索結果ヘッダー */}
              {isMobile ? renderMobileResultHeader() : renderDesktopResultHeader()}

              {/* 検索結果テーブル */}
              <SearchTable
                searchResults={companies}
                onDashboardClick={onDashboardClick}
                selectedCompanies={selectedCompanies}
                onCompanyClick={onCompanyClick}
                addAllCompanies={addAllCompanies}
                registeredCompanies={registeredCompanies}
                setCompanyDescription={setCompanyDescription}
                handleDescriptionClick={handleDescriptionClick}
                isMobile={isMobile}
              />
            </>
          )
        )}
      </div>
    </div>
  )
}

export default RelatedWordSearch
